import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import React from "react";
import { useTranslation } from "react-i18next";
import { Lesson } from "../../common/api";
import { Button, Form, Modal } from "react-bootstrap";
import { ApiContext } from "../../common/context/ApiContext";
import { useLoadingIndicator } from "../../common/context/LoadingIndicatorContext";
import {
  AnalyticsContext,
  EditFlashpackMenuDeleteButtonTapEvent,
  EditFlashpackMenuRenameButtonTapEvent,
  EditFlashpackMenuSettingsButtonTapEvent,
} from "../../common/context/AnalyticsContext";

export const FlashpackEditMenu = ({
  anchor,
  isOpen,
  onShowSettings,
  onFlashpackDeleted,
  onFlashpackRenamed,
  onClose,
  lesson,
}: {
  anchor: HTMLElement;
  isOpen: boolean;
  onShowSettings: () => void;
  onFlashpackDeleted: () => void;
  onFlashpackRenamed: () => void;
  onClose: () => void;
  lesson: Lesson;
}) => {
  const { t } = useTranslation();
  const { modifyCustomFlashpack, deleteCustomFlashpack } =
    React.useContext(ApiContext);
  const { startLoading, stopLoading } = useLoadingIndicator(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = React.useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);

  const [tempLessonTitle, setTempLessonTitle] = React.useState(lesson.title);
  const { logEvent } = React.useContext(AnalyticsContext);

  const doRename = React.useCallback(() => {
    if (tempLessonTitle && lesson.custom_flashpack_id !== null) {
      startLoading();
      modifyCustomFlashpack(lesson.custom_flashpack_id, {
        title: tempLessonTitle,
      }).then(() => {
        stopLoading();
        setIsRenameModalOpen(false);
        onFlashpackRenamed();
      });
    }
  }, [
    lesson,
    startLoading,
    modifyCustomFlashpack,
    stopLoading,
    setIsRenameModalOpen,
    tempLessonTitle,
    onFlashpackRenamed,
  ]);

  const doDelete = React.useCallback(() => {
    if (lesson.custom_flashpack_id !== null) {
      startLoading();
      deleteCustomFlashpack(lesson.custom_flashpack_id).then(() => {
        stopLoading();
        setIsDeleteModalOpen(false);
        onFlashpackDeleted();
      });
    }
  }, [
    lesson,
    startLoading,
    deleteCustomFlashpack,
    stopLoading,
    setIsDeleteModalOpen,
    onFlashpackDeleted,
  ]);

  return (
    <>
      <Menu
        open={isOpen}
        onClose={onClose}
        anchorEl={anchor}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        disableAutoFocusItem
        slotProps={{
          paper: {
            style: {
              width: 250,
              marginTop: 6,
            },
          },
        }}
      >
        <MenuItem
          onClick={() => {
            logEvent(
              new EditFlashpackMenuSettingsButtonTapEvent(
                lesson.id,
                lesson.custom_flashpack_id!
              )
            );
            onClose();
            onShowSettings();
          }}
        >
          <ListItemText>{t("flashpacks_edit_settings")}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            logEvent(
              new EditFlashpackMenuRenameButtonTapEvent(
                lesson.id,
                lesson.custom_flashpack_id!
              )
            );
            onClose();
            setIsRenameModalOpen(true);
          }}
        >
          <ListItemText>{t("flashpacks_edit_rename")}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            logEvent(
              new EditFlashpackMenuDeleteButtonTapEvent(
                lesson.id,
                lesson.custom_flashpack_id!
              )
            );
            onClose();
            setIsDeleteModalOpen(true);
          }}
        >
          <ListItemText>{t("flashpacks_edit_delete")}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Rename modal */}
      <Modal
        show={isRenameModalOpen}
        onHide={() => {
          setIsRenameModalOpen(false);
          onClose();
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("flashpacks_edit_rename_title")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>{t("flashpacks_edit_rename_description")}</Form.Label>
              <Form.Control
                placeholder={lesson.title}
                autoFocus
                value={tempLessonTitle}
                onChange={(e) => setTempLessonTitle(e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setIsRenameModalOpen(false);
              onClose();
            }}
          >
            {t("generic_cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={doRename}
            disabled={!tempLessonTitle || lesson.custom_flashpack_id === null}
          >
            {t("flashpacks_edit_rename")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete modal */}
      <Modal
        show={isDeleteModalOpen}
        onHide={() => {
          setIsDeleteModalOpen(false);
          onClose();
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("flashpacks_edit_delete_title")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t("flashpacks_edit_delete_description")}</Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setIsDeleteModalOpen(false);
              onClose();
            }}
          >
            {t("generic_cancel")}
          </Button>
          <Button variant="danger" onClick={doDelete}>
            {t("flashpacks_edit_delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
